import React, { useState, useMemo } from "react";
import { fullName } from "../../lib/format";
import { PhotoUploader } from "../shared/PhotoUploader";
import { CoverImage } from "../shared/CoverImage";
import { TextStyleControls } from "../shared/TextStyleControls";
import { Avatar } from "../shared/Avatar";

// DepartmentFormModal — create / edit a department (name, description, logo, priority, leader).
// Leader is now selected from USERS (not members). Linked-member name/photo is used for display.
export default function DepartmentFormModal({ existing, members = [], users = [], isSuperAdmin = false, onSave, onClose }) {
  const [form, setForm] = useState(() => existing ? {
    name: existing.name || "",
    description: existing.description || "",
    logoUrl: existing.logoUrl || "",
    coverImage: existing.coverImage || "",
    textColor: existing.textColor || "white",
    textEffect: existing.textEffect || "shadow",
    overlay: typeof existing.overlay === "number" ? existing.overlay : 55,
    priority: existing.priority || "medium",
    leaderUserId: existing.leaderUserId || "",
    isDonations: !!existing.isDonations,
  } : {
    name: "",
    description: "",
    logoUrl: "",
    coverImage: "",
    textColor: "white",
    textEffect: "shadow",
    overlay: 55,
    priority: "medium",
    leaderUserId: "",
    isDonations: false,
  });
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState("");
  const [leaderSearch, setLeaderSearch] = useState("");
  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (k === "name" && nameError) setNameError("");
  };

  // Resolve a user to a display object (linked-member name/photo preferred, else username/initials).
  const userDisplay = (u) => {
    if (!u) return null;
    const linkedMember = u.memberId ? members.find(m => m.id === u.memberId) : null;
    return {
      id: u.id,
      username: u.username,
      name: linkedMember ? fullName(linkedMember) : (u.name || u.username),
      photo: (linkedMember && linkedMember.photo) || u.photo || null,
      isLinked: !!linkedMember,
    };
  };

  const userDisplays = useMemo(() => (users || []).map(userDisplay).filter(Boolean),
    [users, members]);

  const filteredUsers = useMemo(() => {
    const q = leaderSearch.trim().toLowerCase();
    return (userDisplays || [])
      .filter(d => !q || d.name.toLowerCase().includes(q) || (d.username || "").toLowerCase().includes(q))
      .slice(0, 25);
  }, [userDisplays, leaderSearch]);

  const selectedLeader = form.leaderUserId ? userDisplays.find(d => d.id === form.leaderUserId) : null;

  const submit = async () => {
    if (!form.name.trim()) return;
    setNameError(""); setSaving(true);
    try {
      const result = await onSave(form);
      if (result && result.ok) { onClose(); return; }
      if (result && result.error) {
        const msg = String(result.error || "").toLowerCase();
        if (msg.includes("already exists") || msg.includes("already taken")) {
          setNameError("A department with this name already exists. Please choose a different name.");
        } else {
          setNameError(result.error);
        }
      }
    } catch (e) {
      setNameError(e.message || "Could not save");
    }
    setSaving(false);
  };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" data-testid="dept-form-modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{existing ? "Edit Department" : "New Department"}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group mb-14">
            <label>Name *</label>
            <input
              data-testid="dept-name"
              value={form.name}
              onChange={e => set("name", e.target.value)}
              placeholder="e.g. Worship"
              autoFocus
              style={nameError ? { borderColor: "var(--kc-accent)" } : undefined}
              aria-invalid={!!nameError}
              aria-describedby={nameError ? "dept-name-error" : undefined}
            />
            {nameError && (
              <div
                id="dept-name-error"
                data-testid="dept-name-error"
                role="alert"
                style={{
                  marginTop: 8,
                  background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)",
                  border: "1px solid color-mix(in srgb, var(--kc-accent) 45%, transparent)",
                  color: "var(--kc-accent)",
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 12,
                  fontWeight: 600,
                  lineHeight: 1.4,
                }}
              >
                {nameError}
              </div>
            )}
          </div>
          <div className="form-group mb-14">
            <label>Description</label>
            <textarea data-testid="dept-description" value={form.description} onChange={e => set("description", e.target.value)} rows={2} />
          </div>
          <div className="form-grid mb-14">
            <div className="form-group">
              <label>Priority</label>
              <select data-testid="dept-priority" value={form.priority} onChange={e => set("priority", e.target.value)}>
                <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
            </div>
          </div>
          <div className="form-group mb-14">
            <label>Cover Image</label>
            <CoverImage
              value={form.coverImage || ""}
              onChange={(v) => set("coverImage", v || "")}
              editable={isSuperAdmin}
              title={form.name || "Department"}
              iconName="people-group"
              overlay={typeof form.overlay === "number" ? form.overlay : 55}
              textColor={form.textColor || "white"}
              textEffect={form.textEffect || "shadow"}
              data-testid="dept-cover-image"
            />
            <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6 }}>
              JPG, PNG, or WEBP up to 5 MB. Displays at 16:9 above the department card.
            </div>
            {isSuperAdmin && (
              <TextStyleControls
                textColor={form.textColor || "white"}
                textEffect={form.textEffect || "shadow"}
                overlay={typeof form.overlay === "number" ? form.overlay : 55}
                onColor={(v) => set("textColor", v)}
                onEffect={(v) => set("textEffect", v)}
                onOverlay={(v) => set("overlay", v)}
                previewImage={form.coverImage || ""}
                previewTitle={form.name || "Department"}
                previewSubtitle={form.description || "Description will appear here"}
              />
            )}
          </div>
          <div className="form-group mb-14">
            <label>Department Logo</label>
            <PhotoUploader
              value={form.logoUrl || null}
              name={form.name || "Department"}
              onChange={(v) => set("logoUrl", v || "")}
              size={72}
              data-testid="dept-logo"
            />
            <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6 }}>
              Upload a square image (PNG or JPG, up to 2 MB). Remove to revert to the gold default placeholder.
            </div>
          </div>
          <div className="form-group">
            <label>Department Leader</label>
            {selectedLeader ? (
              <div data-testid="dept-leader-selected" style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: 8 }}>
                <Avatar name={selectedLeader.name} photo={selectedLeader.photo} memberId={selectedLeader.memberId} style={{ width: 28, height: 28, fontSize: 10 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: "var(--kc-text)", fontWeight: 600 }}>{selectedLeader.name}</div>
                  <div style={{ fontSize: 10, color: "var(--kc-meta)" }}>@{selectedLeader.username}</div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  data-testid="dept-leader-clear"
                  onClick={() => { set("leaderUserId", ""); setLeaderSearch(""); }}
                >Change</button>
              </div>
            ) : (
              <>
                <input
                  placeholder="Search users by username or name…"
                  value={leaderSearch}
                  onChange={e => setLeaderSearch(e.target.value)}
                  data-testid="dept-leader-search"
                  style={{ marginBottom: 6 }}
                />
                <div data-testid="dept-leader-results" style={{ maxHeight: 200, overflowY: "auto", border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)", borderRadius: 8 }}>
                  <button
                    type="button"
                    data-testid="dept-leader-result-none"
                    onClick={() => set("leaderUserId", "")}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", background: "transparent", color: "var(--kc-sub)", border: 0, borderBottom: "1px solid rgba(255,255,255,0.04)", cursor: "pointer", fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontStyle: "italic" }}
                  >— None —</button>
                  {filteredUsers.map(d => (
                    <button
                      key={d.id}
                      type="button"
                      data-testid={`dept-leader-result-${d.id}`}
                      onClick={() => { set("leaderUserId", d.id); setLeaderSearch(""); }}
                      style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "8px 12px", background: "transparent", color: "var(--kc-text)", border: 0, borderBottom: "1px solid rgba(255,255,255,0.04)", cursor: "pointer", fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}
                    >
                      <Avatar name={d.name} photo={d.photo} memberId={d.memberId} style={{ width: 24, height: 24, fontSize: 10 }} />
                      <span style={{ display: "flex", flexDirection: "column" }}>
                        <span>{d.name}</span>
                        <span style={{ fontSize: 10, color: "var(--kc-meta)" }}>@{d.username}</span>
                      </span>
                    </button>
                  ))}
                  {filteredUsers.length === 0 && (
                    <div style={{ padding: "10px 12px", color: "var(--kc-meta)", fontSize: 12 }}>No matching users.</div>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="form-group" style={{ marginTop: 14, borderTop: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)", paddingTop: 14 }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                data-testid="dept-isDonations"
                checked={!!form.isDonations}
                onChange={e => set("isDonations", e.target.checked)}
              />
              <span style={{ fontSize: 13 }}>Enable as Donations module</span>
            </label>
            <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6, lineHeight: 1.45 }}>
              When enabled, this department&apos;s card opens the specialised Donations workspace instead of the standard detail page. Public listing + private intake / distribution / community-needs registry.
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-gold" data-testid="dept-save" disabled={!form.name.trim() || saving} onClick={submit}>
            {saving ? "Saving…" : (existing ? "Save" : "Create")}
          </button>
        </div>
      </div>
    </div>
  );
}
